import { Utils } from 'versafleet-core';
import * as Sentry from '@sentry/browser';

const { request, convertDataToParams } = Utils.RequestHelper;
const { Helper } = Utils;

const baseFilter = {
  per_page: 20,
  sort_by: 'id',
  order_by: 'desc',
};

function receiveTaskValidateSuccessful() {
  return {
    type: 'RECEIVE_TASK_VALIDATE_SUCCESSFUL',
  };
}

function receiveTask(task) {
  return {
    type: 'RECEIVE_TASK',
    task,
  };
}

function receiveTasks(result, timestamp) {
  return {
    type: 'RECEIVE_TASKS',
    tasks: result.tasks,
    meta: result.meta,
    timestamp,
  };
}

function receiveTasksDetails(result) {
  return {
    type: 'RECEIVE_TASKS_DETAILS',
    tasks: result.tasks,
  };
}

function requestAddingTag() {
  return {
    type: 'REQUEST_ADDING_TAG',
  };
}

function receiveAddingTag() {
  return {
    type: 'RECEIVE_ADDING_TAG',
  };
}

function receiveTaskUpdateSuccessful(task) {
  return {
    type: 'RECEIVE_TASK_UPDATE_SUCCESSFUL',
    task,
  };
}

function receiveTaskUpdateFailed(error) {
  return {
    type: 'RECEIVE_TASK_UPDATE_FAILED',
    error,
  };
}

function receiveArchivedTasks(result, timestamp) {
  return {
    type: 'RECEIVE_ARCHIVED_TASKS',
    tasks: result.tasks,
    meta: result.meta,
    timestamp,
  };
}

function receiveTotalFailedTasksCount(count) {
  return {
    type: 'RECEIVE_TOTAL_FAILED_TASKS_COUNT',
    count,
  };
}

function receiveFailedTasks(failedTasks) {
  return {
    type: 'RECEIVE_FAILED_TASKS',
    failedTasks,
  };
}

function receiveExportTags(exportTags) {
  return {
    type: 'RECEIVE_EXPORT_TAGS',
    exportTags,
  };
}

function receiveExportTaskCountSuccessful(taskCount) {
  return {
    type: 'RECEIVE_EXPORT_TASK_COUNT_SUCCESSFUL',
    taskCount,
  };
}

function receiveResetExportTaskCount() {
  return {
    type: 'RECEIVE_RESET_EXPORT_TASK_COUNT',
  };
}

function receiveSystemTags(result) {
  return {
    type: 'RECEIVE_SYSTEM_TAGS',
    systemTags: result,
  };
}

function receiveOptionAddresses(optionAddresses) {
  return {
    type: 'RECEIVE_OPTION_ADDRESSES',
    optionAddresses,
  };
}

function setFilter(filter) {
  return {
    type: 'SET_TASK_FILTER',
    filter,
  };
}

function fetch(id) {
  return dispatch => request(`/tasks/${id}`, {
    method: 'GET',
  })
    .then((result) => {
      dispatch(receiveTask(result.task));
      return result;
    })
    .catch((error) => { Sentry.captureException(error); });
}

function fetchList(filter) {
  return (dispatch, getState) => {
    dispatch(setFilter(filter));
    const timestamp = new Date();
    const state = getState();
    return request(`/tasks${convertDataToParams({
      page: state.task.taskMeta.page,
      ...baseFilter,
      ...state.task.filter,
      archived: false,
    })}`, {
      method: 'GET',
    })
      .then((result) => {
        dispatch(receiveTasks(result, timestamp));
        return result;
      });
  };
}

function fetchArchivedList(filter) {
  return (dispatch, getState) => {
    dispatch(setFilter(filter));
    const timestamp = new Date();
    const state = getState();
    return request(`/tasks${convertDataToParams({
      page: state.task.archivedTaskMeta.page,
      ...baseFilter,
      ...state.task.filter,
      archived: true,
    })}`, {
      method: 'GET',
    })
      .then((result) => {
        dispatch(receiveArchivedTasks(result, timestamp));
        return result;
      });
  };
}

function fetchTaskSystemTags() {
  return dispatch => request('/tasks/system_tags', {
    method: 'GET',
  })
    .then((result) => {
      dispatch(receiveSystemTags(result.tags));
      return result.tags;
    });
}

function fetchCompletionHistories(ids, batch = 50) {
  return (dispatch, getState) => {
    const state = getState();
    const idsToLoad = !Helper.isNullOrUndefined(ids)
      ? ids
      : state.task.tasksIds
        .map(taskId => state.task.tasks[taskId])
        .filter(task => Helper.isNullOrUndefined(task.task_completion_histories))
        .map(task => task.id);

    for (let i = 0; i < idsToLoad.length; i += batch) {
      const queryString = Utils.RequestHelper.convertDataToParams(
        { ids: idsToLoad.slice(i, i + batch) },
      );
      request(`/tasks/completion_histories${queryString}`, {
        method: 'GET',
      }).then(result => dispatch(receiveTasksDetails(result)));
    }
  };
}

function create(query) {
  return dispatch => request('/tasks', {
    method: 'POST',
  }, {
    task: query,
  })
    .then((result) => {
      dispatch(receiveTaskUpdateSuccessful(result.task));
      dispatch(fetchList());
      return result;
    });
}

function update(id, query) {
  return dispatch => request(`/tasks/${id}`, {
    method: 'PUT',
  }, {
    task: query,
  })
    .then((result) => {
      dispatch(receiveTaskUpdateSuccessful(result.task));
      return result;
    });
}

function tagMultiple(ids, query) {
  return (dispatch) => {
    dispatch(requestAddingTag());
    return request('/tasks/update_multiple', {
      method: 'PUT',
    }, {
      ids,
      action_type: 'update',
      task: {
        tag_list: query,
      },
    })
      .then((result) => {
        dispatch(receiveAddingTag());
        dispatch(fetchList());
        return result;
      })
      .catch(error => dispatch(receiveTaskUpdateFailed(error)));
  };
}

function archive(id) {
  return () => request(`/tasks/${id}/archive`, {
    method: 'PUT',
  })
    .catch((error) => { Sentry.captureException(error); });
}

function unarchive(id) {
  return dispatch => request(`/tasks/${id}/unarchive`, {
    method: 'PUT',
  })
    .then(() => {
      dispatch(fetchArchivedList());
    });
}

function archiveMultiple(ids) {
  return () => request('/tasks/update_multiple', {
    method: 'PUT',
  }, {
    ids,
    action_type: 'archive',
  })
    .catch((error) => { Sentry.captureException(error); });
}

function unarchiveMultiple(ids) {
  return dispatch => request('/tasks/update_multiple', {
    method: 'PUT',
  }, {
    ids,
    action_type: 'unarchive',
  })
    .then((result) => {
      dispatch(fetchArchivedList());
      return result;
    })
    .catch((error) => { Sentry.captureException(error); });
}

async function printMultiple(query) {
  const result = await request('/tasks/multiple_print', {
    method: 'POST',
  }, {
    task: query,
  });
  return result;
}

async function printTaskLabelMultiple(query) {
  const result = await request('/tasks/multiple_task_label', {
    method: 'POST',
  }, {
    task: query,
  });
  return result;
}

function validate(query) {
  return dispatch => request('/tasks/validate', {
    method: 'POST',
  }, {
    task: query,
  })
    .then((result) => {
      dispatch(receiveTaskValidateSuccessful());
      return result;
    });
}

function fetchTotalFailedTasksCount() {
  return dispatch => request('/tasks/total_failed_count', {
    method: 'GET',
  })
    .then((result) => {
      dispatch(receiveTotalFailedTasksCount(result.count));
      return result;
    });
}

function fetchFailedTasks() {
  return dispatch => request('/tasks/failed_count', {
    method: 'GET',
  })
    .then((result) => {
      dispatch(receiveFailedTasks(result.tasks));
      return result;
    });
}

function fetchExportTags() {
  return dispatch => request('/tasks/export/tags', {
    method: 'GET',
  })
    .then((result) => {
      dispatch(receiveExportTags(result.export_tags));
      return result;
    });
}

async function exportTasks(query) {
  const result = await request('/tasks/export', {
    method: 'POST',
  }, {
    task: query,
  });
  return result;
}

function checkExportTaskCount(data) {
  return dispatch => request('/tasks/export/task_count', {
    method: 'POST',
  }, {
    ...data,
  })
    .then((result) => {
      dispatch(receiveExportTaskCountSuccessful(result.task_count));
      return result;
    })
    .catch((error) => { Sentry.captureException(error); });
}

function resetExportTaskCount() {
  return (dispatch) => {
    dispatch(receiveResetExportTaskCount());
  };
}

function _delete(id) {
  return dispatch => request(`/tasks/${id}`, {
    method: 'DELETE',
  })
    .then((result) => {
      dispatch(fetchArchivedList());
      return result;
    })
    .catch((error) => { Sentry.captureException(error); });
}

function multipleDelete(ids) {
  return dispatch => request('/tasks/multiple_delete', {
    method: 'DELETE',
  }, {
    ids,
  })
    .then((result) => {
      dispatch(fetchArchivedList());
      return result;
    })
    .catch((error) => { Sentry.captureException(error); });
}

function cancel(id) {
  return dispatch => request(`/tasks/${id}/cancel`, {
    method: 'PUT',
  })
    .then(() => {
      dispatch(fetch(id));
    });
}

// Date can be a string or array. YYYY-MM-DD format
// Will fetch all the addresses of the tasks within the date range.
function fetchAddressesOptionList(date) {
  return async (dispatch) => {
    const queryString = Utils.RequestHelper.convertDataToParams(
      { date },
    );
    let result = await Utils.RequestHelper.request(
      `/tasks/task_contact_person/list${queryString}`, { method: 'GET' },
    );
    // `result` can have duplicate results
    result = Helper.removeDuplicates(result.tasks, 'contact_person');
    dispatch(receiveOptionAddresses(result));
  };
}

export default {
  setFilter,
  fetch,
  fetchList,
  fetchArchivedList,
  fetchTaskSystemTags,
  fetchCompletionHistories,
  create,
  update,
  tagMultiple,
  archive,
  unarchive,
  archiveMultiple,
  unarchiveMultiple,
  printMultiple,
  printTaskLabelMultiple,
  validate,
  fetchTotalFailedTasksCount,
  fetchFailedTasks,
  fetchExportTags,
  exportTasks,
  checkExportTaskCount,
  resetExportTaskCount,
  delete: _delete,
  multipleDelete,
  cancel,
  fetchAddressesOptionList,
};
